import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTvChannelPing } from "@/api/tv";
import { useElementOnScreen } from "@/hooks/useVideoOnScreen";
import { isVideoUrlExpired } from "@/utils/video";

import type Hls from "hls.js";

type IChannelPingPipContext = {
  channelId: number | null;
  hls: Hls | null;
  setChannelId: React.Dispatch<React.SetStateAction<number | null>>;
  setDelay: React.Dispatch<React.SetStateAction<number>>;
  setHls: React.Dispatch<React.SetStateAction<Hls | null>>;
  handleChangeLivePip: (
    isPip: boolean,
    newHls: Hls,
    newChannelId: number,
  ) => void;
};

const ChannelPingPipContext = createContext<any>({
  videoElement: null,
  isPip: false,
});

interface ChannelPingPipProviderProps {
  children: React.ReactNode;
  videoElement: HTMLVideoElement | null;
}

const ChannelPingPipProvider: React.FC<ChannelPingPipProviderProps> = ({
  children,
  videoElement,
}) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [hls, setHls] = useState<Hls | null>(null);

  const isVideoOnScreen = useElementOnScreen(videoElement);

  const [channelId, setChannelId] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [delay, setDelay] = useState<number>(0);
  const [location, setLocation] = useState<string | null>(null);

  const { refetch, dataUpdatedAt } = useTvChannelPing(channelId as number, {
    isEnabled: isPlaying && !!channelId,
    delay,
  });

  const handlePause = () => {
    setIsPlaying(false);
    hls?.stopLoad();
  };

  const handlePlay = () => {
    setIsPlaying(true);
    hls?.startLoad();
    if (isVideoUrlExpired(dataUpdatedAt)) {
      refetch();
    }
  };

  const handleChangeLivePip = (
    isPip: boolean,
    newHls: Hls,
    newChannelId: number,
  ) => {
    setHls(isPip ? newHls : null);
    setChannelId(isPip ? newChannelId : null);
  };

  useEffect(() => {
    if (videoElement !== videoRef.current) {
      videoRef.current = videoElement;
      setLocation(document.location.href);
    }
  }, [videoElement, videoRef.current]);

  useEffect(() => {
    if (!videoElement) {
      setIsPlaying(false);
      if (channelId) {
        setChannelId(null);
      }
      if (channelId && !isVideoOnScreen) {
        hls?.stopLoad();
      }
      if (location !== document.location.href) {
        hls?.destroy();
      }
    }
  }, [videoElement, hls]);

  useEffect(() => {
    if (videoElement && channelId) {
      videoElement.addEventListener("pause", handlePause);
      videoElement.addEventListener("play", handlePlay);
    }
    return () => {
      if (videoElement && channelId) {
        videoElement.removeEventListener("pause", handlePause);
        videoElement.removeEventListener("play", handlePlay);
      }
    };
  }, [channelId, videoElement, isVideoOnScreen]);

  const value = useMemo(
    () => ({
      channelId,
      hls,
      setChannelId,
      setDelay,
      setHls,
      handleChangeLivePip,
    }),
    [channelId, hls],
  );

  return (
    <ChannelPingPipContext.Provider value={value}>
      {children}
    </ChannelPingPipContext.Provider>
  );
};

const useChannelPingPipContext = (): IChannelPingPipContext =>
  useContext(ChannelPingPipContext);

export {
  ChannelPingPipProvider,
  useChannelPingPipContext,
  type IChannelPingPipContext,
};
